import React, { ReactNode } from 'react';

interface SectionLayoutProps {
  children: ReactNode;
  className?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' | number;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | string;
  background?: 'transparent' | 'card' | 'card-2';
  id?: string;
  fullWidth?: boolean;
}

const SectionLayout: React.FC<SectionLayoutProps> = ({
  children,
  className = '',
  maxWidth = '2xl',
  padding = 'lg',
  background = 'transparent',
  id,
  fullWidth = false,
}) => {
  const getMaxWidth = () => {
    if (typeof maxWidth === 'number') {
      return `${maxWidth}px`;
    }
    const maxWidthMap = {
      sm: '640px',
      md: '768px',
      lg: '1024px',
      xl: '1200px',
      '2xl': '1400px',
      full: '100%',
    };
    return maxWidthMap[maxWidth] || '1400px';
  };

  const getPadding = () => {
    if (typeof padding === 'string' && (padding.includes('px') || padding.includes('rem'))) {
      return padding;
    }
    const paddingMap = {
      none: '0',
      // Padding vertikal + horizontal agar konten tidak menempel ke sisi layar
      sm: '40px 32px',
      md: '56px 40px',
      lg: '80px 56px',
      xl: '120px 64px',
    };
    return paddingMap[padding as keyof typeof paddingMap] || '120px 64px';
  };

  const getBackground = () => {
    const backgroundMap = {
      transparent: 'transparent',
      card: 'var(--card, rgba(255, 255, 255, 0.02))',
      'card-2': 'var(--card-2, rgba(255, 255, 255, 0.02))',
    };
    return backgroundMap[background];
  };

  const containerStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: fullWidth ? '100%' : getMaxWidth(),
    margin: '0 auto',
    padding: getPadding(),
    background: getBackground(),
  };

  return (
    <>
      <style>{`
        .section-layout {
          width: 100%;
        }

        .section-layout-container {
          transition: all 0.3s ease;
          box-sizing: border-box;
        }

        /* Utility classes untuk konsistensi section */
        .section-header {
          margin-bottom: 80px;
          text-align: left;
        }

        .section-title {
          font-size: clamp(40px, 6vw, 72px);
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1.1;
          margin-bottom: 24px;
          text-align: left;
        }

        .section-label {
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: var(--muted-2, rgba(255, 255, 255, 0.6));
          margin-bottom: 16px;
          text-align: left;
        }

        .section-description {
          font-size: 18px;
          color: var(--muted-2, rgba(255, 255, 255, 0.6));
          max-width: 600px;
          line-height: 1.7;
          text-align: left;
        }

        @media (max-width: 1024px) {
          .section-layout-container {
            padding-left: 32px !important;
            padding-right: 32px !important;
          }
        }

        @media (max-width: 768px) {
          .section-layout-container {
            padding: 80px 24px !important;
          }

          .section-header {
            margin-bottom: 60px;
          }

          .section-description {
            font-size: 16px;
          }
        }
      `}</style>
      <section
        id={id}
        className={`section-layout ${className}`}
      >
        <div
          className="section-layout-container"
          style={containerStyle}
        >
          {children}
        </div>
      </section>
    </>
  );
};

export default SectionLayout;

